<template>
  <el-card>
    <el-radio-group v-model="activeTab" class="desktop">
      <el-radio-button v-for="axe in axes" :key="axe.value" :value="axe.value">
        {{ axe.title }}
      </el-radio-button>
    </el-radio-group>
    <div ref="chartdiv" style="height: 500px" />
  </el-card>
</template>

<script lang="ts">
import useResize from '@/hooks/useResize'

import {
  defineComponent,
  onMounted,
  onBeforeUnmount,
  ref,
  shallowRef,
  watch
} from 'vue'

import { am4core, am4charts } from '@/utils/async_chart_loader'

export default defineComponent({
  name: 'RealtimeChart',
  props: {
    data: {
      type: Object,
      required: true
    },
    axes: {
      type: Array,
      required: true
    },
    start: {
      type: Number,
      default: 0.8
    }
  },
  setup(props, context) {
    const chartdiv = ref(null)
    const chartBox = shallowRef()
    const activeTab = ref('impressions')

    const renderChart = (type: string) => {
      if (!chartdiv.value) return
      clearChart()
      // @ts-ignore: Object is possibly 'null'.
      let chart = am4core.create(chartdiv.value, am4charts.XYChart)
      chart.hiddenState.properties.opacity = 0 // this creates initial fade-in
      chart.responsive.enabled = true
      const currentData = props.data[type] || []

      const data = currentData.map((item: any) => {
        return {
          ...item,
          datetime: new Date(item.datetime)
        }
      })

      chart.data = data

      let dateAxis = chart.xAxes.push(new am4charts.DateAxis())
      dateAxis.start = props.start
      dateAxis.keepSelection = true

      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
      if (valueAxis.tooltip) {
        valueAxis.tooltip.disabled = true
      }
      valueAxis.title.text = type

      let series = chart.series.push(new am4charts.LineSeries())
      series.dataFields.dateX = 'datetime'
      series.dataFields.valueY = 'today'
      series.tooltipText = 'Today: [bold]{valueY}[/]'
      series.fillOpacity = 0.3
      series.tensionX = 0.77
      series.stroke = am4core.color('#1890ff')
      series.fill = am4core.color('#1890ff')
      series.yAxis = valueAxis

      let series2 = chart.series.push(new am4charts.LineSeries())
      series2.dataFields.dateX = 'datetime'
      series2.dataFields.valueY = 'yesterday'
      series2.tooltipText = 'Yesterday: [bold]{valueY}[/]'
      series2.fillOpacity = 0.3
      series2.tensionX = 0.77
      series2.stroke = am4core.color('#a3a3a3')
      series2.fill = am4core.color('#a3a3a3')
      series2.yAxis = valueAxis

      chart.cursor = new am4charts.XYCursor()
      chart.cursor.lineY.opacity = 0
      const scrollbarX = new am4charts.XYChartScrollbar()
      scrollbarX.series.push(series)
      scrollbarX.series.push(series2)
      chart.scrollbarX = scrollbarX
      chartBox.value = chart
    }

    const clearChart = () => {
      if (chartBox.value && !chartBox.value.isDisposed) {
        chartBox.value.dispose()
        chartBox.value = null
      }
    }

    watch(activeTab, (value: string, _oldValue) => {
      renderChart(value)
    })

    onMounted(() => {
      renderChart(activeTab.value)
    })

    onBeforeUnmount(() => {
      clearChart()
    })

    watch(
      () => props.axes,
      (newVal) => {
        activeTab.value = newVal[0].value
      }
    )

    const { isDesktop } = useResize()

    return { isDesktop, chartdiv, activeTab }
  }
})
</script>

<style lang="scss" scoped>
.el-card {
  padding: 0;
  margin-top: 10px;
}
</style>
